import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import { customValidator, FieldValidator } from 'core/utils/fieldValidators'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import React, { useEffect, useMemo, useState } from 'react'

const { kubernetes } = ApiClient.getInstance()

const CLUSTER_NAME_MAX_LENGTH = 22
export const clusterNameValidator = new FieldValidator(
  (clusterName) => /^[a-z]([-a-z0-9]*[a-z0-9])?$/.test(clusterName),
  'Cluster name is invalid',
)

const ClusterNameField = ({ params, getParamsUpdater, clusters }) => {
  const classes = useStyles()
  const [allClusters, setAllClusters] = useState([])
  const [loading, setLoading] = useState(false)

  // Fetch all clusters from all regions for validation
  const fetchAllClusters = async () => {
    try {
      setLoading(true)
      const allClusters = await kubernetes.getClustersFromAllRegions()
      setAllClusters(allClusters)
    } catch (error) {
      console.error('Error fetching clusters:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllClusters()
  }, [])

  // Single region validation - check if cluster name already exists in the current region
  const clusterNameUniqueValidatorSingleRegion = useMemo(() => {
    return customValidator((value: string) => {
      if (!value) {
        return true
      }
      return !clusters?.find((cluster) => cluster?.metadata?.name === value)
    }, 'A cluster with same name already exists')
  }, [clusters])

  // Multi region validation - check if cluster name already exists in another region
  const clusterNameUniqueValidatorMultiRegion = useMemo(() => {
    return customValidator((value: string) => {
      if (!value) {
        return true
      }
      return !allClusters?.find((cluster) => cluster?.metadata?.name === value)
    }, 'A cluster with same name already exists in another region')
  }, [allClusters])

  return (
    <div className={classes.finalTouchesSection}>
      <TextField
        id="clusterName"
        label="Cluster Name"
        placeholder={`Cluster Name (Max ${CLUSTER_NAME_MAX_LENGTH} characters)`}
        value={params?.clusterName}
        onChange={getParamsUpdater('clusterName')}
        maxLength={CLUSTER_NAME_MAX_LENGTH}
        validations={[
          clusterNameValidator,
          clusterNameUniqueValidatorSingleRegion,
          clusterNameUniqueValidatorMultiRegion,
        ]}
        info={
          <>
            Cluster name must consist of lower case alphanumeric characters or '-', start with an
            alphabetic character, and end with an alphanumeric character (e.g. 'my-name', or
            'abc-123'). Maximum length is {CLUSTER_NAME_MAX_LENGTH} characters.
          </>
        }
        crossOrigin={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        disabled={loading}
        required
      />
      {loading && <FontAwesomeIcon spin>spinner</FontAwesomeIcon>}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  finalTouchesSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),

    '& > i': {
      marginTop: theme.spacing(3),
    },
  },
}))

export default ClusterNameField
